import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'

import { apphistory, AppState } from '../../../store'
import { AdditionalFormNames, AllAdditionalFieldsNames, AllBasedFieldsNames, FormNames } from '../../../models/form/IFormDefinition'
import useFormLogic from '../../../components/form/hooks/useFormLogic'
import { TFieldValuesObject } from '../../../components/form/FormProps'
import { incomeInfoForm } from '../../../formDefinitions/IncomeInfoForm/IncomeInfoForm'
import { incomeAdditionalInfoFieldsDefinition } from '../../../formDefinitions/IncomeInfoForm/IncomeInfoFields'
import { additionalIncomeForm } from '../../../formDefinitions/IncomeInfoForm/AdditionalIncomeForm'
import { FormSubmitionService } from '../../../service/FormSubmitionService'
import { STORAGEKEYS } from '../../../common/keys'
import { AppLinks } from '../../../environment/app-links'
import { SessionStorage } from '../../../common/cookie-storage'
import { IIncomeInfoFormLogicReturnModel } from '../models/IFormStepsModel'
import { IAdditionalLoanDataModel } from '../../../models/form/IFormSubmitionModel'
import { SET_APP_HAS_ERROR } from '../../../store/actions/application-actions'
import analyticsService from '../../../service/analytics.service'
import { WorkflowTypes } from '../../../common/workflow'
import { collectBikData } from '../../../common/helpers/collectBikData'
import { DateTime } from 'luxon'



/**
 * Custom hook z logiką obsługującą pierwszą stronę formularza.
 * @returns 
 */
const useIncomeInfoFormLogic = (): IIncomeInfoFormLogicReturnModel => {
	const dispatch = useDispatch()
   const incomeInfoState = useSelector((state: AppState) => state.formState[FormNames.IncomeInfoForm])
	const formState = useSelector((state: AppState) => state.formState)
	const appConsents = useSelector((state: AppState) => state.consentState.appConsents)
   const loanCalculatorState = useSelector((state: AppState) => state.loanCalculatorState)
	const ANALYTICS_EVENT_TYPES_NAMES = useSelector((state: AppState) => state.lenConfigState.analyticsEventTypesNames)

	const sessionStorage = new SessionStorage()
   const [showAdditionalForm, setShowAdditionalForm] = useState<boolean>(formState.globalFormConfig.currentOpenedAdditionalForms.includes(AdditionalFormNames.AdditionalIncomeForm))
   const { handleValidateForm } = useFormLogic()



	useEffect(() => { 
   	const formFillStartingTime = sessionStorage.get(STORAGEKEYS.FORM_FILL_STARTING_TIME)

		// WYKONUJEMY KOD TYLKO RAZ PRZY PIERWSZYM RENDERZE STRONY
		if (!formFillStartingTime) {
      	sessionStorage.add(STORAGEKEYS.FORM_FILL_STARTING_TIME, DateTime.now().toISO())
   	}
	}, [])

   const currentIncomeInfoFields = useMemo(() => {
		if(incomeInfoState.formConfig?.currentIncomeInfoFields !== undefined) {
			const newIncomeInfoFields: Record<string, any> = {}
	
		  	incomeInfoState.formConfig.currentIncomeInfoFields.forEach(fieldName => {
				newIncomeInfoFields[fieldName] = incomeAdditionalInfoFieldsDefinition[fieldName]
		  	})
		  
			return {
				...incomeInfoForm.fields,
				...newIncomeInfoFields,
			}
		}
		
		return { ...incomeInfoForm.fields }
	  	
	}, [incomeInfoState.formConfig?.currentIncomeInfoFields])


	const handleOnIncomeInfoFormSubmit = async(fieldValuesObject: TFieldValuesObject<Partial<typeof AllBasedFieldsNames>> | TFieldValuesObject<Partial<typeof AllAdditionalFieldsNames>>) => {
		const basicFormSubmitResult = handleValidateForm({formName: incomeInfoForm.name, fieldValuesObject, formValidationFunctionsObject: incomeInfoForm.formValidationFunctions })

		const additionalFormSubmitResult = showAdditionalForm ? // Jeśli dodatkowy formularz otwarty - walidujemy również go
			handleValidateForm({
				formName: additionalIncomeForm.name,
				fieldValuesObject,
				formValidationFunctionsObject: additionalIncomeForm.formValidationFunctions,
			})
		: true

		if(basicFormSubmitResult && additionalFormSubmitResult){
			const formSubmitionService = new FormSubmitionService()
			const IS_FROM_OPENA_FORM = WorkflowTypes.isFromOpenaForm()
			
			let additionalLoanData: IAdditionalLoanDataModel = {
				loanAmount: loanCalculatorState.requestedAmount,
				loanInstalments: loanCalculatorState.instalments,
				hdim: null,
				jsc: null,
				fillTime: null
			}
			
			// [ZAŁOŻENIE BIZNESOWE: 83031] GDY UŻYTKOWNIK PRZECHODZI PROCES ZA POMOCĄ FORMULARZA OPENY, WYSYŁAMY HDIM I JSC. W PRZYPADKU INTEGRATORA ZEWNĘTRZNEGO, WYSYŁAMY JE NA INNYM ETAPIE
			if(IS_FROM_OPENA_FORM) {      
				const [hdim, jsc] = collectBikData()
				const formFillStartingTime = DateTime.fromISO(sessionStorage.get(STORAGEKEYS.FORM_FILL_STARTING_TIME))
         	const TOTAL_FORM_FILL_TIME =  DateTime.now().diff(formFillStartingTime, 'milliseconds').milliseconds

				additionalLoanData = {
					...additionalLoanData,
					hdim, jsc, fillTime: TOTAL_FORM_FILL_TIME
				}
			}
				
			const formSubmitionData = formSubmitionService.mapFieldsToSubmitionModel(formState, appConsents, additionalLoanData)
			apphistory.push(AppLinks.offerWaiting)


			try {
				const submissionResponse = await formSubmitionService.submit(formSubmitionData) /*Składanie wniosku */
				sessionStorage.add(STORAGEKEYS.SET_ALPHA_ID, submissionResponse.alphaId) //Zapisujemy zwrócony przez Backend numer wniosku w session storage
				return
			}
			catch (e: any) {
				const ERROR_ANALYTICS_DATA = {
					id: analyticsService.DISPLAY_ERROR_PAGE_IDS.INCOME_INFO_FORM_PAGE_SUBMIT_APPLICATION,
					reason: `Błąd przy składaniu wniosku, po wypełnieniu ostatniej strony formularza: (${ AppLinks.incomeInfoForm }).`,
					details: {
						description: e.message,
						errorDataFromBackend: e?.data,
					},
				}
				
				dispatch({ type: SET_APP_HAS_ERROR }) //Ustawiamy globalny błąd aplikacji co spowoduje przejście na ekran z błędem
				await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.FrontendErrorPageDisplayed, JSON.stringify(ERROR_ANALYTICS_DATA, undefined, 3))
			}
		}
	}


   return {
      showAdditionalForm,
      incomeInfoState,
      currentIncomeInfoFields,

      handleOnIncomeInfoFormSubmit,
      setShowAdditionalForm,
   }
}

export default useIncomeInfoFormLogic